import { CSSProperties, FormEvent, useMemo, useRef, useState } from 'react'
import { Form, Tabs, Schema, Uploader, SelectPicker, toaster, Message } from 'rsuite'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'
import { List } from '@rsuite/icons'

import { FileType } from 'rsuite/esm/Uploader'
import Loading, { EnumLoadingType } from '@/client/components/Loading'
import Format, { TextField } from '@client/components/FormControls'
import useQuery from '@client/utils/useQuery'
import { PanelError } from '@client/components/ErrorProvider'
import OutTemplates from '@client/models/outTemplate'
import ModalDialog from '@client/components/ModalDialog'
import { TOutTableColumnMapping } from '@/client/models/outTableColumn'
import { IconButton } from '@/client/components/Controls'
import { baseName } from '@/shared'
import { syncTranslation } from '@/client/utils/translate'
import toastMessage from '@/client/utils/toastMessage'
import error from '@client/stores/errorStore'

type TTemplateFormData = any
// type TTemplateFormData = {
//     templateName: string,
//     fileName: string,
//     excel?: string,
//     sheets?: any[],
//     checkAsync?: any,
// }

type TFormat = {
    field?: string
    range?: any[]
    length?: number
    decimal?: number
    select?: any[]
    format?: string
    default?: string | number
    trueValue?: string
    falseValue?: string
}
type TFieldType = { value: string; label: string; format: TFormat }
type THeaderFieldType = { field: string, style: CSSProperties }

type UpdateTemplateProps = {
    db: string
    id: string
    type: string
    setOpenDialog: (openDialog: boolean) => void
    onUpload: (uploadedFile: any) => void
}
type FormFieldTypeProps = { sheetIndex: number; mappingIndex: number; mapping: TOutTableColumnMapping }

const UploadContentStyle = {
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    padding: '0.75em 0.5em',
} as CSSProperties
const UploadButtonStyle = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
} as CSSProperties
const FooterStyle = {}
const FooterErrorWrapperStyle = {}
const FooterErrorStyle = {}
const FormStyle = {
    display: 'flex',
    flex: 1,
    marginTop: '1em',
    marginBottom: '0',
} as CSSProperties
const TabSheetStyle = {
    flexFlow: 'column nowrap',
} as CSSProperties
const TabRowStyle = {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    gap: '1em',
} as CSSProperties
const TabRowHeaderStyle = {
    ...TabRowStyle, ...{
        fontWeight: 'bold',
    }
} as CSSProperties
const TabRowLabelStyle = {
    minWidth: '11em',
    width: '15em',
    wordBreak: 'break-all',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: '1' /* number of lines to show */,
    lineClamp: '1',
    WebkitBoxOrient: 'vertical',
    textAlign: 'left',
} as CSSProperties
const LabelStyle = {
    width: '3em',
} as CSSProperties
const FirstFieldLabelStyle = {
    marginLeft: 5,
    marginRight: 5,
    minWidth: '6em',
} as CSSProperties
const SecondFieldLabelStyle = {
    marginLeft: 15,
    marginRight: 5,
} as CSSProperties
const FieldTypeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    width: '100%',
    margin: '2px 0',
} as CSSProperties
const FieldTypeInputStyle = {
    background: 'var(--color-4)',
    color: 'var(--color-9)',
    border: 'none',
    flex: 1,
} as CSSProperties
const FieldTypeBooleanStyle = {
    background: 'var(--color-4)',
    color: 'var(--color-9)',
    border: 'none',
    flex: 1,
} as CSSProperties
const SelectPickerStyle = {
    borderRadius: 0,
    //minWidth: '8em',
    minWidth: '6em',
    flex: 1,
} as CSSProperties
const HeaderFieldStyle = {
    minWidth: '11em',
    width: '15em',
} as CSSProperties
const HeaderTypeStyle = {
    minWidth: '6em',
} as CSSProperties
const HeaderFormatStyle = {
    width: '100%',
} as CSSProperties

const { StringType, ArrayType } = Schema.Types

const outTemplate = new OutTemplates()

let fieldTypes: TFieldType[] = []
let headerNames: THeaderFieldType[] = []
syncTranslation(() => {
    fieldTypes = [
        {
            value: 'string',
            label: t('Text'),
            format: {
                range: [1, 1024],
                default: 50,
            } as TFormat,
        },
        {
            value: 'number',
            label: t('Number'),
            format: {
                range: [0, 5],
                default: 2,
            },
        },
        {
            value: 'boolean',
            label: t('Boolean'),
            format: {
                range: [],
                default: '',
            },
        },
        {
            value: 'date',
            label: t('Date'),
            format: {
                range: [],
                default: 'dd.mm.yyyy',
            },
        },
        {
            value: 'select',
            label: t('Selection'),
            format: {
                range: [],
                default: '',
            },
        },
    ]

    headerNames = [
        { field: t('Field'), style: HeaderFieldStyle },
        { field: t('Type'), style: HeaderTypeStyle },
        { field: t('Format'), style: HeaderFormatStyle },
    ]
})

// const getFieldType = (type: string) => {
//     return fieldTypes.find(item => {
//         return item.value == type
//     }) as TFieldType
// }

export default function UpdateTemplate({ db, id, type, setOpenDialog, onUpload }: UpdateTemplateProps) {
    const { t } = useTranslation()
    const newRecord = type == 'add'
    const format = Format()
    const formRef = useRef<TTemplateFormData>(undefined)
    const originalRef = useRef<TTemplateFormData>(undefined)
    const activeFieldRef = useRef<string>('-1-0-0')
    const [modified, setModified] = useState<number>(0)
    const [uploading, setUploading] = useState<boolean>(false)
    const [formError, setFormError] = useState({})
    // Why the server refused the file or the save. postData answers undefined
    // then and leaves the reason in the error store, which nothing here showed:
    // a file without B1/C1 crashed on `upload.fileName` with the spinner left
    // running, and a version missing a sheet left the dialog open in silence.
    const [rejected, setRejected] = useState<string>('')
    const [activeTab, setActiveTab] = useState<string>('key-1')
    const { data, loading, error: readError } = useQuery(outTemplate.read, {
        args: { params: [db, newRecord ? '' : id] },
        allowEmptyVariables: false,
    })
    const [formData, setFormData] = useState((current: any) => {
        if (current) return current
        if (data) return data
        return {}
    })

    const buttons = [
        { status: 'save', label: t('Save'), disabled: modified == 0 },
        { status: 'cancel', label: t('Cancel') },
    ]

    if (readError) {
        return <PanelError message={readError.message + '\n' + readError.stack} />
    }

    // Shown here instead, so it is taken out of the store: left there, it
    // would come up as a notice as well.
    function takeRejection() {
        setRejected(error.take() || t('An error has occurred:'))
    }

    if (!newRecord) {
        if (!formData && data) {
            setFormData(data)
            console.log('edit record: ', data)
            originalRef.current = data
        }
        if (!formData || loading) return <Loading />
    }

    // async function projectNameExists  (id: string, projectName: string, newRecord: boolean) {
    //     let retCode = false
    //     if (!newRecord || projectName) {
    //         const result = await template.getProjectByName(projectName, id)
    //         if (result?.data) retCode = true
    //     }
    //     return retCode
    // }

    function hasErrors() {
        const count = Object.keys(formError).length
        if (count > 0) console.log('UpdateTemplate.hasErrors', formError, count)
        return count > 0
    }

    function validateUploadData(fileName: string, upload: any) {
        if (!fileName || !upload) return false

        if (formData) {
            if (formData.excel && formData.excel != baseName(fileName, false)) {
                alert(t('The template has not the same file name.'))
                return false
            }

            const keys: any = Object.keys(upload)

            let difSheets = false
            formData.sheets?.forEach((item: any) => {
                const id: string = item._id

                let found = false
                keys.forEach((key: string) => {
                    if (upload[key].tableId == id) found = true
                })
                if (!found) difSheets = true
            })
            if (difSheets) {
                alert(t('The template is different.'))
                return false
            }
        }

        return true
    }

    function mergeUploadedData(fileName: string, upload: any, uploadFile: string) {
        if (!fileName || !upload) return

        const newFormData: any = {
            templateName: '',
            fileName: uploadFile,
            excel: fileName,
            sheets: [],
        }

        // The file decides which columns there are and in which order (the server
        // has already given them their keys); a column known before keeps the type
        // and format set for it. This used to start from the whole stored list and
        // append every match a second time, so known columns came back twice.
        const _mergeColumns = (currentData: any, uploadData: any) => {
            const known = [...(currentData.mapping || [])]
            const mapping = uploadData.mapping.map((col: any) => {
                const index = known.findIndex((item: any) => item.field == col.field)
                if (index < 0) return col
                const [item] = known.splice(index, 1)
                return { ...col, type: item.type, format: item.format }
            })

            currentData._id = uploadData.tableId
            currentData.name = uploadData.tableName
            currentData.mapping = [...mapping]
            return currentData
        }
        const _mergeSheet = (sheet: any) => {
            let found = false
            formData?.sheets?.forEach((item: any) => {
                if (sheet.tableId == item._id) {
                    newFormData.sheets.push(_mergeColumns(item, sheet))
                    found = true
                }
            })
            if (!found) {
                newFormData.sheets.push(_mergeColumns({ mapping: [] }, sheet))
            }
        }

        const props = Object.keys(upload)
        props?.forEach((key) => {
            _mergeSheet(upload[key])
        })

        setFormData(newFormData)
    }

    function handleDialogButtons(status: string) {
        if (status === 'cancel') {
            setOpenDialog(false)
            return true
        }

        if (status === 'save') {
            if (modified) {
                onApplyClick()
                return false
            }
        }

        // removeTab(modifiedDialog?.key)
        setOpenDialog(false)
        return true
    }

    async function handleOnChangeUpload(fileList: FileType[]) {
        if (fileList.length == 0) return

        setUploading(true)
        setRejected('')

        const file: any = fileList[fileList.length - 1]
        console.log('Database name: ', db)
        console.log('File to upload: ', file)

        let formData = new FormData()
        formData.append('template', file.blobFile)

        const result = await outTemplate.checkUpload(db, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            //onUploadProgress,
        })
        console.log('check result: ', result)

        const upload = result?.data
        if (!upload) {
            takeRejection()
            setUploading(false)
            return
        }
        const uploadFile = upload.fileName || ''
        if (uploadFile) delete upload.fileName

        if (validateUploadData(file.name, upload)) {
            mergeUploadedData(file.name, upload, uploadFile)
            setModified((modified) => modified + 1)
        }

        setUploading(false)
    }

    function hasAutoFocus(sheet: number, mapping: number, index: number = 0) {
        // if (activeFieldRef.current == `${sheet}-${mapping}-${index}`)
        //     console.log('hasAutoFocus', activeFieldRef.current, sheet, mapping, index, activeFieldRef.current == `${sheet}-${mapping}-${index}`)
        return activeFieldRef.current == `${sheet}-${mapping}-${index}`
    }

    function setActiveField(sheet: number, mapping: number, index: number = 0) {
        const activeField = `${sheet}-${mapping}-${index}`
        if (activeFieldRef.current != activeField) {
            activeFieldRef.current = activeField
            // console.log('setActiveField', activeFieldRef.current, sheet, mapping, index)
        }
    }

    // function handleUpdateCompleted(response: any, file: FileType, event: ProgressEvent<EventTarget>, xhr: XMLHttpRequest): void {
    //     if (response.type == 'server_error') {
    //         if (xhr.status == 401) {
    //             console.log('call refresh token')
    //         }
    //         console.log('ERROR', response)
    //         return
    //     }
    //     console.log('SUCCESS', response)
    // }

    // const config = getTokenHeader({ headers: {} })

    // const params = {
    //     dbName: db,
    // }

    function onApplyClick() {
        const saveData = { ...formData }
        const id = saveData._id || ''

        // delete all not necessary fields
        // but not if new record
        // if (id) {
        //     const keys = ['__v']
        //     keys.forEach(key => {
        //         if (originalRef.current[key] === saveData[key]) {
        //             delete (saveData[key])
        //         }
        //         saveData.sheets.forEach((sheet: any) => {
        //             if (typeof sheet[key] !== 'undefined') delete (sheet[key])
        //         })
        //     })
        // }

        formRef.current?.checkAsync().then(async (result: any) => {
            if (!result.hasError) {
                const result =
                    newRecord || !id
                        ? await outTemplate.create(db, saveData)
                        : await outTemplate.update(db, id, saveData)
                if (result) {
                    onUpload(result)
                    toastMessage(t('Successfully saved'))
                    setOpenDialog(false)
                    setUploading(false)
                } else {
                    takeRejection()
                }
            }
        })
    }

    const UploadFile = () => {
        let style = { ...UploadContentStyle }
        if (!formData?.fileName) {
            style.textAlign = 'center'
            style.justifyContent = 'center'
        }
        return (
            <Uploader
                name="template"
                action="/templates/out/check"
                //headers={config.headers}
                //data={params}
                accept=".xls,.xlsx,.xml,.csv"
                autoFocus={true}
                multiple={false}
                autoUpload={false}
                withCredentials={false}
                fileListVisible={false}
                onChange={handleOnChangeUpload}
                // onSuccess={handleUpdateCompleted}
                // onError={handleUpdateCompleted}
                draggable
            >
                <div style={style}>
                    <span style={UploadButtonStyle}>
                        {formData?.excel ? formData.excel : t('Click or Drag file to this area to upload.')}
                    </span>
                </div>
            </Uploader>
        )
    }

    const MappingField = ({ sheetIndex, mappingIndex, mapping }: FormFieldTypeProps) => {
        const format = mapping.format as TFormat
        const selectedType = mapping.type
        //const [multiSelectActive, setMultiSelectActive] = useState<boolean>(false)
        const currentMapping = `sheets[${sheetIndex}].mapping.[${mappingIndex}]`

        function handleModified(value: any, e: any) {
            // const element = e.target
            // const sheet = parseInt(element.attributes['data-sheet'].value)
            // const mapping = parseInt(element.attributes['data-map'].value)
            // const index = parseInt(element.attributes['data-map-sub']?.value) || 0
            // setActiveField(sheet, mapping, index)
            // setModified((modified) => modified + 1)
        }

        const StringField = () => {
            return (
                <div style={FieldTypeStyle}>
                    <span style={FirstFieldLabelStyle}>{t('Length')}</span>
                    <Form.Control
                        type="text"
                        name={`${currentMapping}.format.length`}
                        style={FieldTypeInputStyle}
                        autoFocus={hasAutoFocus(sheetIndex, mappingIndex)}
                        onFocus={() => setActiveField(sheetIndex, mappingIndex)}
                        onChange={handleModified}
                        data-sheet={sheetIndex}
                        data-map={mappingIndex}
                        alt={type}
                    />
                </div>
            )
        }
        const NumberField = () => {
            return (
                <div style={FieldTypeStyle}>
                    <span style={FirstFieldLabelStyle}>{t('Decimals')}</span>
                    <Form.Control
                        type="number"
                        name={`${currentMapping}.format.decimal`}
                        style={FieldTypeInputStyle}
                        autoFocus={hasAutoFocus(sheetIndex, mappingIndex)}
                        onFocus={() => setActiveField(sheetIndex, mappingIndex)}
                        onChange={handleModified}
                        data-sheet={sheetIndex}
                        data-map={mappingIndex}
                        alt={type}
                    />
                </div>
            )
        }
        const BooleanField = () => {
            return (
                <div style={FieldTypeStyle}>
                    <span style={FirstFieldLabelStyle}>{t('True')}</span>
                    <Form.Control
                        type="text"
                        name={`${currentMapping}.format.trueValue`}
                        style={FieldTypeBooleanStyle}
                        autoFocus={hasAutoFocus(sheetIndex, mappingIndex, 0)}
                        onFocus={() => setActiveField(sheetIndex, mappingIndex, 0)}
                        onChange={handleModified}
                        data-sheet={sheetIndex}
                        data-map={mappingIndex}
                        data-map-sub={0}
                    />

                    <span style={SecondFieldLabelStyle}>{t('False')}</span>
                    <Form.Control
                        type="text"
                        name={`${currentMapping}.format.falseValue`}
                        style={FieldTypeBooleanStyle}
                        autoFocus={hasAutoFocus(sheetIndex, mappingIndex, 1)}
                        onFocus={() => setActiveField(sheetIndex, mappingIndex, 1)}
                        onChange={handleModified}
                        data-sheet={sheetIndex}
                        data-map={mappingIndex}
                        data-map-sub={1}
                    />
                </div>
            )
        }
        const DateField = () => {
            return (
                <div style={FieldTypeStyle}>
                    <span style={FirstFieldLabelStyle}>{t('Format')}</span>
                    <Form.Control
                        type="text"
                        name={`${currentMapping}.format.format`}
                        style={FieldTypeInputStyle}
                        autoFocus={hasAutoFocus(sheetIndex, mappingIndex)}
                        onFocus={() => setActiveField(sheetIndex, mappingIndex)}
                        onChange={handleModified}
                        data-sheet={sheetIndex}
                        data-map={mappingIndex}
                    />
                </div>
            )
        }
        const SelectField = () => {
            function handleOnEditSelection(e: Event) {
                //const element = e.target as any
                alert('Edit not implemented')
            }

            function handleOnMultiSelect() {
                formData.sheets[sheetIndex].mapping[mappingIndex].format.multiSelect =
                    !formData.sheets[sheetIndex].mapping[mappingIndex].format.multiSelect
                setModified((modified) => modified + 1)
            }

            return (
                <div style={FieldTypeStyle}>
                    <span style={FirstFieldLabelStyle}>{t('List')}</span>
                    <SelectPicker
                        style={SelectPickerStyle}
                        id={mapping.field}
                        placeholder="..."
                        value={formData.sheets[sheetIndex].mapping[mappingIndex].type}
                        data={format.select as any[]}
                        cleanable={false}
                        searchable={false}
                        readOnly
                        /* @ts-ignore */
                        onClick={handleOnEditSelection}
                    />
                    <IconButton
                        title={t('Multiselect')}
                        //name={`${currentMapping}.multiSelect`}
                        active={formData.sheets[sheetIndex].mapping[mappingIndex].format.multiSelect}
                        icon={<List />}
                        appearance="subtle"
                        /* @ts-ignore */
                        onClick={handleOnMultiSelect}
                        data-sheet={sheetIndex}
                        data-map={mappingIndex}
                    />
                </div>
            )
        }
        const EmptyField = () => {
            if (selectedType) return null

            return <div style={FieldTypeStyle}></div>
        }

        const renderField = () => {
            switch (selectedType) {
                case 'string':
                    return <StringField />
                case 'number':
                    return <NumberField />
                case 'boolean':
                    return <BooleanField />
                case 'date':
                    return <DateField />
                case 'select':
                    return <SelectField />
                default:
                    return <EmptyField />
            }
        }

        return (
            <div style={FieldTypeStyle}>
                <SelectPicker
                    style={SelectPickerStyle}
                    value={mapping.type}
                    placeholder={t('Select type ...')}
                    data={fieldTypes}
                    cleanable={false}
                    searchable={false}
                    plaintext
                />

                {renderField()}
            </div>
        )
    }

    const TemplateName = () => {
        function handleModified(value: any, e: any) {
            // const element = e.target
            // const sheet = parseInt(element.attributes['data-sheet'].value)
            // const mapping = parseInt(element.attributes['data-map'].value)
            // const index = parseInt(element.attributes['data-map-sub']?.value) || 0
            // setActiveField(sheet, mapping, index)
            // setModified((modified) => modified + 1)
        }

        if (!formData?.fileName) return null
        return (
            <TextField
                name={'templateName'}
                label={t('Template name')}
                autoFocus={hasAutoFocus(-1, 0)}
                onFocus={() => setActiveField(-1, 0)}
                onChange={handleModified}
            />
        )
    }

    const Sheets = () => {
        function handleTabChanged(eventKey: string | number | undefined): void {
            setActiveTab(eventKey as string)
        }

        return (
            <Tabs defaultActiveKey={activeTab} onSelect={handleTabChanged} appearance="tabs">
                {formData?.sheets?.map((sheet: any, sheetIndex: any) => {
                    const key = 'key-' + (sheetIndex + 1)
                    return (
                        <Tabs.Tab key={sheetIndex} eventKey={key} title={sheet.name}>
                            <Form.Group controlId={`name-${key}`}>
                                <span style={TabRowStyle}>
                                    <span style={LabelStyle}>{t('Id')}</span>
                                    <span>{sheet._id}</span>
                                </span>
                            </Form.Group>

                            <h4>{t('Colunns')}</h4>
                            <Form.Group controlId={`columns-${sheetIndex + 1}`} className="upload-template-sheet" style={TabSheetStyle}>
                                <span key="column-header" style={TabRowHeaderStyle}>
                                    {headerNames.map((item, i: number) =>
                                        <span key={i} style={item.style} title={item.field}>{item.field}</span>
                                    )}
                                </span>
                                {sheet.mapping.map((m: TOutTableColumnMapping, i: number) => (
                                    <span key={i} style={TabRowStyle}>
                                        <Form.Label style={TabRowLabelStyle} title={m.field}>
                                            {m.field}
                                        </Form.Label>
                                        <MappingField sheetIndex={sheetIndex} mappingIndex={i} mapping={m} />
                                    </span>
                                ))}
                            </Form.Group>
                        </Tabs.Tab>
                    )
                })}
            </Tabs>
        )
    }

    const model = Schema.Model({
        // name: StringType().addAsyncRule((value, data) => {
        //     return true
        //     // return new Promise(resolve => {
        //     //     const _check = async () => {
        //     //         let retCode = true
        //     //         if (!newRecord || value) {
        //     //             const result: any = await user.findtUserByNameOrEmail(value, data?._id || '')
        //     //             if (result?.data) retCode = false
        //     //         }
        //     //         console.error('addAsyncRule: ', retCode)
        //     //         resolve(retCode)
        //     //     }
        //     //     _check()
        //     // })
        // }, t('Template already in use') as string)
        //     .isRequired(format.required(t('Template'))),
        templateName: StringType().isRequired(format.required(t('Template name'))),
        excel: StringType().isRequired(format.required(t('Excel name'))),
        fileName: StringType().isRequired(format.required(t('File name'))),
        //sheet: ArrayType().isRequired(format.required(t('Sheet(s)'))),
    })

    const formComponent = useMemo(
        () => (
            <Form
                fluid
                layout="horizontal"
                className="form-distance form-controll-full-width"
                ref={formRef}
                formValue={formData}
                onChange={setFormData}
                model={model}
                nestedField
            >
                <UploadFile />
                <TemplateName />
                <Sheets />

                <div style={FooterStyle}>
                    {hasErrors() && (
                        <span style={FooterErrorWrapperStyle}>
                            <span style={FooterErrorStyle}>{t('Check your inputs')}</span>
                        </span>
                    )}
                </div>
            </Form>
        ),
        [formData]
    )

    return (
        <ModalDialog
            title={t(newRecord ? 'Upload template' : 'Edit template')}
            buttons={buttons}
            onClick={handleDialogButtons}
            size="md"
        //autoScroll={false}
        >
            {formComponent}

            {rejected && <PanelError message={rejected} showTitle={false} />}

            {uploading && <Loading type={EnumLoadingType.Spinner} overlay={false} />}

            {/* <JSONView formValue={formData} formError={formError} /> */}
        </ModalDialog>
    )
}
